import type {
  ArrayValues,
  HasReadonlyKeys,
  LiteralUnion,
  IsTuple,
  Exact,
  AllUnionFields,
  Writable,
} from 'type-fest';
import type {CanBeReadonly, HasLiteralValues} from '../../utils';

export type OptionsMapType = {
  labels: Record<string, {index: number}>;
  values: Record<string, {index: number}>;
}; // right now just returning index, but other information might be useful.

type OptionBase = {
  /**
   * Disables the option
   */
  isDisabled?: boolean;
  /**
   * Visible text for the option
   */
  label: string;
  /**
   * The actual value stored
   */
  value: string;
  options?: never;
  shouldHideGroupLabel?: never;
  meta?: object;
};

type OptionGroupBase<TOptionBase extends OptionBase = OptionBase> = {
  /**
   * Visible text for the option group
   */
  label: string;
  /**
   * An array of objects, with each object being an option
   */
  options: CanBeReadonly<Array<TOptionBase>>;
  /**
   * Hides the group label
   */
  shouldHideGroupLabel?: boolean;
  value?: never;
  meta?: object;
};

/**
 * This Utility type enables the creation of BaseOptionType with extended props.
 * This is useful for when a selection type needs to have additional content.
 *
 * Please be aware that if you are passing in ExtendedProps they _must_ use either CanBeReadonly or CanBeReadonlyDeep.
 * This is to support allowing either `as const` or mutable objects.
 * Some types will cause issues when passed through CanBeReadonlyDeep.
 *
 * For example, `ButtonPrefixSuffix` will cause a type error, because the ReadonlyDeep will deeply recurse into the React types themselves.
 * This causes a problem with things like `dangerouslySetInnerHTML`, causing a type error when we try to use them.
 *
 * There may be a more elegant solution to this, but I haven't figured it out yet.
 */
export type BaseOptionType<
  ExtendedProps extends object | undefined = undefined,
> = ExtendedProps extends object
  ? CanBeReadonly<OptionBase> & ExtendedProps
  : CanBeReadonly<OptionBase>;

/**
 * This Utility type enables the creation of a BaseOptionGroupType.
 * It allows users to have extended props for the option group, as well as the options themselves.
 * This is useful for when a selection type needs to have additional content.
 *
 * Please be aware that if you are passing in ExtendedProps they _must_ use either CanBeReadonly or CanBeReadonlyDeep.
 * This is to support allowing either `as const` or mutable objects.
 * Some types will cause issues when passed through CanBeReadonlyDeep.
 *
 * For example, `ButtonPrefixSuffix` will cause a type error, because the ReadonlyDeep will deeply recurse into the React types themselves.
 * This causes a problem with things like `dangerouslySetInnerHTML`, causing a type error when we try to use them.
 *
 * There may be a more elegant solution to this, but I haven't figured it out yet.
 */
export type BaseOptionGroupType<
  TOptionBase extends OptionBase = OptionBase,
  ExtendedProps extends object | undefined = undefined,
> = ExtendedProps extends object
  ? CanBeReadonly<OptionGroupBase<TOptionBase>> & ExtendedProps
  : CanBeReadonly<OptionGroupBase<TOptionBase>>;

/**
 * This Utility type enables the creation of a BaseOptionsType.
 * It allows users to have custom option and option group types.
 */
export type BaseOptionsType<
  TOption extends BaseOptionType = BaseOptionType,
  TOptionGroup extends BaseOptionGroupType = BaseOptionGroupType,
> = CanBeReadonly<Array<TOption | TOptionGroup>>;

export type Meta<TOption extends BaseOptionType = BaseOptionType> = {
  /**
   * Whether the maximum number of selections has been reached.
   */
  hasMaxSelections?: boolean;
  /**
   * The option that was selected, if one is available.
   */
  option?: TOption;
  /**
   * The options that are currently selected if the selectionMode is "multiple".
   */
  options?: TOption[];
};

/**
 * Creates the Metadata type associated with SelectionMode onChange handlers.
 */
export type SelectionModeMeta<
  TOptions extends BaseOptionsType | undefined = undefined,
> = TOptions extends BaseOptionsType
  ? /**
     *  If the consuming site has provided us an Options type, then we will be returning it back to them in the onChange handler.
     */
    {
      /**
       * Whether the maximum number of selections has been reached.
       */
      hasMaxSelections?: boolean;
      /**
       * The option that was selected, if one is available.
       */
      option: ExtractOptions<TOptions>;
      /**
       * The options that are currently selected if the selectionMode is "multiple".
       */
      options: ExtractOptions<TOptions>[];
    }
  : {
      /**
       * Whether the maximum number of selections has been reached.
       */
      hasMaxSelections?: boolean;
      /**
       * The option that was selected, if one is available.
       */
      option?: BaseOptionType;
      /**
       * The options that are currently selected if the selectionMode is "multiple".
       */
      options?: BaseOptionType[];
    };

/**
 * Accepts an array of `BaseOptions` and returns the union of all `value` props, including options nested in groups.
 */
type SelectionModeValue<
  TOptions extends BaseOptionsType | undefined = undefined,
> = TOptions extends BaseOptionsType
  ? HasReadonlyKeys<TOptions> extends true
    ? ExtractOptionValues<ArrayValues<TOptions>>
    : LiteralUnion<ExtractOptionValues<ArrayValues<TOptions>>, string>
  : string;

interface SelectionModeSharedProps {
  flattenedOptions?: BaseOptionType[];
  optionsMap?: OptionsMapType;
  getIndexByValue?: (value: string) => number | undefined;
  getIndexByLabel?: (label: string) => number | undefined;
}

export interface SelectionModeSingle<
  TOptions extends BaseOptionsType | undefined = undefined,
  ExtendedMeta extends object | undefined = undefined,
> extends SelectionModeSharedProps {
  /**
   * Sets the selection mode
   */
  selectionMode: 'single';
  /**
   * The currently selected value. Can be a `string` for `single`, `string[]` for `multiple`, or `never` for `none`.
   */
  value?: string;
  /**
   * Callback function triggered when the selection changes.
   */
  onChange?: (
    value: SelectionModeValue<TOptions>,
    meta: ExtendedMeta extends object
      ? SelectionModeMeta<TOptions> & ExtendedMeta
      : SelectionModeMeta<TOptions>
  ) => void | React.Dispatch<
    React.SetStateAction<SelectionModeValue<TOptions>>
  >;
  /**
   * Callback used to retrieve a full option object (e.g. { label, value })
   * for selected values not present in the `options` array.
   */
  getSelectedOptionByValue?: (
    value: string
  ) => TOptions extends BaseOptionsType
    ? ExtractOptions<TOptions>
    : BaseOptionType | undefined;
  maxSelections?: never;
}

export interface SelectionModeMultiple<
  TOptions extends BaseOptionsType | undefined = undefined,
  ExtendedMeta extends object | undefined = undefined,
> extends SelectionModeSharedProps {
  selectionMode: 'multiple';
  value?: Array<string>;
  onChange?: (
    value: Array<SelectionModeValue<TOptions>>,
    meta: ExtendedMeta extends object
      ? SelectionModeMeta<TOptions> & ExtendedMeta
      : SelectionModeMeta<TOptions>
  ) => void | React.Dispatch<
    React.SetStateAction<Array<SelectionModeValue<TOptions>>>
  >;
  getSelectedOptionByValue?: (
    value: string
  ) => TOptions extends BaseOptionsType
    ? ExtractOptions<TOptions>
    : BaseOptionType | undefined;
  /**
   * The maximum number of selections allowed.
   * Only applies when `selectionMode` is set to `multiple`.
   */
  maxSelections?: number;
}

export type SelectionModeNone = {
  selectionMode?: 'none';
  value?: never;
  onChange?: never;
  flattenedOptions?: never;
  optionsMap?: never;
  getIndexByValue?: never;
  getIndexByLabel?: never;
  getSelectedOptionByValue?: never;
  maxSelections?: never;
};

/**
 * Creates a union of SelectionMode props, including using extended BaseOptions and Meta types for onChange handlers.
 */
export type SelectionModeProps<
  TOptions extends BaseOptionsType | undefined = undefined,
  SingleMeta extends SelectionModeMeta = SelectionModeMeta,
  MultiMeta extends SelectionModeMeta = SingleMeta,
> =
  | SelectionModeSingle<TOptions, SingleMeta>
  | SelectionModeMultiple<TOptions, MultiMeta>
  | SelectionModeNone;

/**
 * Utility Types
 */

/**
 * Takes a `BaseOption | BaseOptionGroup` type and returns a union of the `value` props of all options (including those nested in groups).
 */
export type ExtractOptionValues<TOption> = TOption extends BaseOptionType
  ? TOption['value']
  : TOption extends BaseOptionGroupType
    ? TOption['options'][number]['value']
    : never;

type ExtractOption<TOption> = TOption extends BaseOptionType
  ? TOption
  : TOption extends BaseOptionGroupType
    ? TOption['options'][number]
    : never;
/**
 * Takes a `BaseOptions` type and returns a union of all options (including those nested in groups).
 */
export type ExtractOptions<TOptions extends BaseOptionsType> =
  HasLiteralValues<TOptions[number]> extends true
    ? ExtractOption<TOptions[number]>
    : Writable<AllUnionFields<ExtractOption<TOptions[number]>>>;

/**
 * Type guard for option group types.
 *
 * This will work for any Option type that extends from `BaseOptionType` or `BaseOptionGroupType`.
 */

export const isOptionGroup = (
  option: BaseOptionType | BaseOptionGroupType
): option is BaseOptionGroupType => 'options' in option;

/**
 * Utility type to get the default options type if we aren't given a specific type.
 *
 * If a specific (readonly) type is passed through our generic or if it is a superset of our default type, we will use that.
 * Otherwise, we will pass through our default type for a type.
 *
 * This will allow call-sites to do things like:
 *
 * ```
 * type MyOptions = BaseOptionsType<MyOption, MyOptionGroup>;
 *
 * type MySelectionModes<TOptions extends MyOptions> =
 *  SelectionModeProps<
 *    PassSpecificOptionsOrBase<
 *        MyOptions, TOptions
 *    >
 *  >;
 * ```
 */
export type PassSpecificOptionsOrBase<
  TExtendedOptionsType extends BaseOptionsType,
  TOptions extends TExtendedOptionsType,
> =
  /**
   * If we have a read-only or fixed-length options Array, then we want to extract those exact values.
   */
  IsTuple<TOptions> extends true
    ? TOptions
    : /**
       * If our options array is a Superset of our base type, then we want to use those exact values.
       * However, if it is not a superset, then we want to use our default type.
       *
       * This avoids a case where we narrow our type, even though our incoming options array may expand later (i.e. it isn't read only).
       */
      TOptions extends Exact<TExtendedOptionsType, TOptions>
      ? TExtendedOptionsType
      : TOptions;
